import React, { useState, useEffect } from 'react';
import { Table, Pagination, Tag, Tooltip, Space, Button, Typography, Row, Col } from 'antd';
import { Eye } from 'lucide-react';
import { bp_get_qc_sample_history } from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';

const { Text } = Typography;

// IPC (sent to QC from the operator screen), Final (QC's own sample) or Inline.
export function SamplingTypeTag({ type }) {
  if (type === 'IPC') return <Tag color="purple">IPC</Tag>;
  if (type === 'QC') return <Tag color="gold">Final (QC)</Tag>;
  if (type === 'INLINE') return <Tag color="default">Inline</Tag>;
  return <Tag color="default">{type || '-'}</Tag>;
}

// The recipe's custom sample fields: fixed information plus what the operator filled in.
export function SampleDetailsBlock({ fields }) {
  if (!(fields?.length > 0)) return null;
  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <Text type="secondary" className="text-xs block mb-2">Sample Details</Text>
      <Row gutter={16}>
        {fields.map((f) => (
          <Col span={8} key={f.key}>
            <Text type="secondary" className="text-xs block">{f.label}</Text>
            <Text strong>{f.value || '—'}</Text>
          </Col>
        ))}
      </Row>
    </div>
  );
}

const historyColumns = (onView) => [
  { title: 'Date', dataIndex: 'date_time', key: 'date_time', width: 170, render: (v) => v?.slice(0, 19) || '-' },
  { title: 'Sample Number', dataIndex: 'sample_number', key: 'sample_number', width: 190, render: (v) => v || '-' },
  { title: 'Batch Number', dataIndex: 'batch_number', key: 'batch_number', width: 160 },
  { title: 'Recipe', dataIndex: 'recipe_name', key: 'recipe_name', width: 170 },
  { title: 'Process', dataIndex: 'process_name', key: 'process_name', width: 140, render: (v) => v || '-' },
  { title: 'Material', dataIndex: 'material_name', key: 'material_name', width: 140, render: (v) => v || '-' },
  {
    title: 'Sampling Type',
    dataIndex: 'sampling_type',
    key: 'sampling_type',
    align: 'center',
    width: 120,
    filters: [
      { text: 'IPC', value: 'IPC' },
      { text: 'Final (QC)', value: 'QC' },
    ],
    onFilter: (value, record) => record.sampling_type === value,
    render: (v) => <SamplingTypeTag type={v} />,
  },
  { title: 'Testing Type', dataIndex: 'testing_type', key: 'testing_type', width: 150, render: (v) => v || '-' },
  { title: 'Sampled By', dataIndex: 'sampled_by', key: 'sampled_by', width: 150, render: (v) => v || '-' },
  {
    title: 'Entered Values',
    key: 'values_summary',
    width: 220,
    render: (_, r) => {
      const q = r?.quantity;
      const hasQty = q != null && Number(q) > 0;
      const qtyOk =
        (r?.sampling_min_value == null || Number(q) >= Number(r.sampling_min_value)) &&
        (r?.sampling_max_value == null || Number(q) <= Number(r.sampling_max_value));
      return r?.values?.length > 0 || hasQty ? (
        <Space size={[4, 4]} wrap>
          {hasQty && (
            <Tag color={qtyOk ? 'success' : 'error'}>Quantity: {q}</Tag>
          )}
          {r.values.map((v, i) => (
            <Tag key={i} color={v.in_range === false ? 'error' : v.in_range ? 'success' : 'default'}>
              {v.parameter}: {v.value}{v.unit ? ` ${v.unit}` : ''}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary">No values</Text>
      );
    },
  },
  {
    title: 'Result',
    dataIndex: 'status',
    key: 'status',
    align: 'center',
    width: 110,
    render: (s, r) =>
      r?.exception_comment ? (
        <Tooltip title={`Failed, accepted as an exception: ${r.exception_comment}`}>
          <Tag color="warning">Failed – exception</Tag>
        </Tooltip>
      ) : s === 'Completed' ? (
        <Tag color="success">Passed</Tag>
      ) : (
        <Tag color="error">Failed</Tag>
      ),
  },
  {
    title: 'Action',
    key: 'action',
    align: 'center',
    width: 90,
    fixed: 'right',
    render: (_, record) => (
      <Tooltip title="View Results">
        <Button
          type="text"
          icon={<Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
          onClick={() => onView(record)}
        />
      </Tooltip>
    ),
  },
];

const HistoryDetails = ({ record }) => (
  <div className="px-2 py-1">
    <SampleDetailsBlock fields={record?.custom_fields} />
    {record?.exception_comment ? (
      <div className="mb-3 p-3 rounded-lg border border-yellow-300 bg-yellow-50 text-sm">
        <Text strong>Failed sample accepted as an exception</Text>
        {record.exception_by_name ? <Text> by {record.exception_by_name}</Text> : null}
        <div><Text type="secondary">Reason: </Text><Text>{record.exception_comment}</Text></div>
      </div>
    ) : null}
    {record?.checks?.length > 0 ? (
      <Table
        size="small"
        pagination={false}
        rowKey={(_, i) => i}
        dataSource={record.checks}
        columns={[
          { title: 'Check', dataIndex: 'label', key: 'label' },
          { title: 'Required', dataIndex: 'required', key: 'required', align: 'center' },
          {
            title: 'Actual',
            dataIndex: 'actual',
            key: 'actual',
            align: 'center',
            render: (v, c) => <Tag color={c.ok ? 'success' : 'error'}>{v}</Tag>,
          },
          {
            title: 'Result',
            key: 'result',
            render: (_, c) =>
              c.ok ? <Text type="success">Within range</Text> : <Text type="danger">{c.note || 'Out of range'}</Text>,
          },
        ]}
      />
    ) : record?.values?.length > 0 ? (
      <Table
        size="small"
        pagination={false}
        rowKey={(_, i) => i}
        dataSource={record.values}
        columns={[
          { title: 'Parameter', dataIndex: 'parameter', key: 'parameter' },
          { title: 'Standard', dataIndex: 'standard', key: 'standard', render: (v) => v || '-' },
          {
            title: 'Expected Range',
            key: 'range',
            align: 'center',
            render: (_, v) => `${v.min_value ?? '-'} – ${v.max_value ?? '-'}${v.unit ? ` ${v.unit}` : ''}`,
          },
          {
            title: 'Entered Value',
            key: 'value',
            align: 'center',
            render: (_, v) => (
              <Tag color={v.in_range === false ? 'error' : v.in_range ? 'success' : 'default'}>
                {v.value}{v.unit ? ` ${v.unit}` : ''}
              </Tag>
            ),
          },
        ]}
      />
    ) : (
      <Text type="secondary">No parameter values were recorded for this sample.</Text>
    )}
    {record?.quantity != null && (
      <div className="mt-2 text-sm">
        <Text type="secondary">Sample quantity: </Text>
        <Text strong>{record.quantity}</Text>
      </div>
    )}
    {record?.observation ? (
      <div className="mt-1 text-sm">
        <Text type="secondary">Observation: </Text>
        <Text>{record.observation}</Text>
      </div>
    ) : null}
    {record?.documents_count > 0 && (
      <div className="mt-1 text-sm">
        <Text type="secondary">{record.documents_count} document(s) attached — open View Results to see them.</Text>
      </div>
    )}
  </div>
);

// QC samples that already have a result (passed, or failed with any accepted exception), each
// expandable to its required-vs-actual checks. `onView(record)` opens the full result.
export default function SampleHistory({ search, onView }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    bp_get_qc_sample_history({ page_number: page, ...(search && { search }) }, (res) => {
      setRows(res?.data?.data || []);
      setTotal(res?.data?.obj_count || 0);
    });
  }, [page, search]);

  return (
    <>
      <Table
        size="small"
        bordered
        pagination={false}
        columns={historyColumns(onView)}
        dataSource={rows}
        rowKey="sample_detail_id"
        expandable={{ expandedRowRender: (record) => <HistoryDetails record={record} /> }}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No completed samples yet' }}
      />
      <div className="flex justify-end mt-4">
        <Pagination
          total={total}
          showTotal={(count) => `Total ${count} samples`}
          current={page}
          showSizeChanger={false}
          onChange={setPage}
        />
      </div>
    </>
  );
}
